import { useState, useCallback, useRef } from 'react';

/**
 * useCanvasTool - Hook for managing canvas drawing tools
 *
 * Provides state and handlers for tool-based drawing operations.
 * Manages drawing state, keyboard modifiers, and dimensions display.
 *
 * @param {string} activeTool - The currently active tool ('select', 'rectangle', 'line', etc.)
 * @returns {object} Tool state and handlers
 */
const useCanvasTool = (_activeTool) => {
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Keyboard modifier states
  const [shiftPressed, setShiftPressed] = useState(false);
  const [altPressed, setAltPressed] = useState(false);
  const [ctrlPressed, setCtrlPressed] = useState(false);

  // Reference to the object being drawn
  const drawingObjectRef = useRef(null);

  /**
   * Start drawing operation
   */
  const startDrawing = useCallback((point) => {
    setIsDrawing(true);
    setStartPoint(point);
    setCurrentPoint(point);
    setDimensions({ width: 0, height: 0 });
  }, []);

  /**
   * Update drawing during mouse move
   */
  const updateDrawing = useCallback((point) => {
    if (!isDrawing) return;
    setCurrentPoint(point);

    // Calculate dimensions
    const width = Math.abs(point.x - startPoint.x);
    const height = Math.abs(point.y - startPoint.y);
    setDimensions({ width, height });
  }, [isDrawing, startPoint]);

  /**
   * Complete drawing operation
   */
  const completeDrawing = useCallback(() => {
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoint(null);
    setDimensions({ width: 0, height: 0 });
    drawingObjectRef.current = null;
  }, []);

  /**
   * Cancel drawing operation
   */
  const cancelDrawing = useCallback(() => {
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoint(null);
    setDimensions({ width: 0, height: 0 });
    drawingObjectRef.current = null;
  }, []);

  /**
   * Handle keyboard modifiers
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Shift') setShiftPressed(true);
    if (e.key === 'Alt') setAltPressed(true);
    if (e.key === 'Control' || e.metaKey) setCtrlPressed(true);
    if (e.key === 'Escape' && isDrawing) cancelDrawing();
  }, [isDrawing, cancelDrawing]);

  const handleKeyUp = useCallback((e) => {
    if (e.key === 'Shift') setShiftPressed(false);
    if (e.key === 'Alt') setAltPressed(false);
    if (e.key === 'Control' || e.key === 'Meta') setCtrlPressed(false);
  }, []);

  return {
    // Drawing state
    isDrawing,
    startPoint,
    currentPoint,
    dimensions,
    drawingObjectRef,

    // Modifier states
    shiftPressed,
    altPressed,
    ctrlPressed,

    // Handlers
    startDrawing,
    updateDrawing,
    completeDrawing,
    cancelDrawing,
    handleKeyDown,
    handleKeyUp,
  };
};

export default useCanvasTool;
